import { Fragment, useCallback, useMemo, useState } from "react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion/accordion";
import { CheckboxField } from "@/components/ui/form/fields/check-box-field/check-box";

import * as Model from "../model";

import type * as Repository from "../../_repository";

const ACCORDION_ID = "option-pms-list-accordion";

type ChildrenProps = {
	filteredOptionPmsActiveListQueryData: Repository.Query.QueryData.OptionPmsList.OptionPmsActiveListQueryData;
};

type Props = {
	optionPmsOptionListQueryData: Repository.Query.QueryData.OptionPmsOptionListQueryData;
	optionPmsActiveListQueryData: Repository.Query.QueryData.OptionPmsList.OptionPmsActiveListQueryData;

	children: ({ filteredOptionPmsActiveListQueryData }: ChildrenProps) => JSX.Element;
};

export const FilterByOptionForOptionPmsPresenter = ({
	optionPmsOptionListQueryData,
	optionPmsActiveListQueryData,
	//
	children,
}: Props) => {
	const {
		//
		viewData,
		filteredOptionPmsActiveListQueryData,
		//
		// 全てのオプションを表示
		onDisplayAllOption,
		// 全てのオプションを非表示
		onHideAllOption,
		// 表示するオプションの追加
		onAddDisplayOption,
		// 表示するオプションの削除
		onRemoveDisplayOption,
	} = Model.UseCase.useInitialized({ optionPmsActiveListQueryData, optionPmsOptionListQueryData });

	/**
	 * 全てのオプションを表示・非表示
	 */
	const handleClickDisplayAllOption = useCallback(
		(checked: boolean) => {
			checked ? onDisplayAllOption() : onHideAllOption();
		},
		[onDisplayAllOption, onHideAllOption],
	);
	/**
	 * オプションの表示・非表示
	 */
	const onClickDisplayOption = useCallback(
		(optionId: string) => (checked: boolean) => {
			checked ? onAddDisplayOption(optionId) : onRemoveDisplayOption(optionId);
		},
		[onAddDisplayOption, onRemoveDisplayOption],
	);

	const RenderChildren = useMemo(
		() => children({ filteredOptionPmsActiveListQueryData }),
		[children, filteredOptionPmsActiveListQueryData],
	);

	const [openAccordion, setOpenAccordion] = useState<string>(ACCORDION_ID);
	const handleOpenAccordionChange = useCallback((value: string) => {
		setOpenAccordion(value);
	}, []);

	return (
		<>
			<div className="space-y-1">
				<p className="text-sm">オプションでの絞り込み検索</p>

				<Accordion type="single" collapsible value={openAccordion} onValueChange={handleOpenAccordionChange}>
					<AccordionItem value={ACCORDION_ID}>
						<AccordionTrigger>
							<p className="text-sm">オプション一覧</p>
						</AccordionTrigger>

						<AccordionContent>
							<div className="space-y-2">
								<div>
									<CheckboxField
										value={viewData.displayAll}
										label="全てを表示"
										handleChange={handleClickDisplayAllOption}
									/>
								</div>

								<div className="grid grid-cols-2 gap-2">
									{viewData.options.map((option, index) => {
										const handleClickDisplayOption = onClickDisplayOption(option.id);
										return (
											<Fragment key={`option-${index}-${option.checked}`}>
												<CheckboxField
													value={option.checked}
													label={option.label}
													handleChange={handleClickDisplayOption}
												/>
												{option.checked}
											</Fragment>
										);
									})}
								</div>
							</div>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>

			{RenderChildren}
		</>
	);
};
